import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion';
import { db } from '../firebase';
import { configDoc } from '../db';

const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || 'lovers-admin-2024';

interface CoupleRecord {
  coupleId: string;
  person1Name: string;
  person1Email: string;
  person2Name: string;
  person2Email: string;
  startDate?: string;
  createdAt?: any;
}

function slug(s: string) {
  return s.toLowerCase()
    .replace(/ğ/g, 'g').replace(/ü/g, 'u').replace(/ş/g, 's')
    .replace(/ı/g, 'i').replace(/ö/g, 'o').replace(/ç/g, 'c')
    .replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
}

function CoupleRow({ c }: { c: CoupleRecord }) {
  const link = `${window.location.origin}/@${c.coupleId}`;
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      style={rowStyles.card}
    >
      <div style={rowStyles.header}>
        <span style={rowStyles.id}>{c.coupleId}</span>
        {c.startDate && (
          <span style={rowStyles.date}>
            {new Date(c.startDate + 'T00:00:00').toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' })}
          </span>
        )}
      </div>
      <div style={rowStyles.persons}>
        <span>{c.person1Name} · {c.person1Email}</span>
        <span>{c.person2Name} · {c.person2Email}</span>
      </div>
      <motion.button whileTap={{ scale: 0.95 }} onClick={copy} style={rowStyles.copyBtn}>
        {copied ? 'Kopyalandı' : 'Özel URL Kopyala'}
      </motion.button>
    </motion.div>
  );
}

const rowStyles: Record<string, React.CSSProperties> = {
  card: {
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 16, padding: '14px 16px',
    display: 'flex', flexDirection: 'column', gap: 8,
  },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  id: { fontFamily: 'monospace', fontSize: 14, color: '#FF6B9D', fontWeight: 600 },
  date: { fontSize: 12, color: 'rgba(255,255,255,0.4)' },
  persons: { display: 'flex', flexDirection: 'column', gap: 2, fontSize: 13, color: 'rgba(255,255,255,0.65)' },
  copyBtn: {
    background: 'rgba(233,30,140,0.12)', border: '1px solid rgba(233,30,140,0.25)',
    borderRadius: 10, padding: '8px 14px', color: '#FF6B9D', fontSize: 13, cursor: 'pointer', textAlign: 'left',
  },
};

export default function AdminPanel() {
  const [authed, setAuthed] = useState(false);
  const [password, setPassword] = useState('');
  const [pwError, setPwError] = useState('');

  // Form
  const [coupleId, setCoupleId] = useState('');
  const [autoId, setAutoId] = useState(true);
  const [p1Name, setP1Name] = useState('');
  const [p1Email, setP1Email] = useState('');
  const [p2Name, setP2Name] = useState('');
  const [p2Email, setP2Email] = useState('');
  const [startDate, setStartDate] = useState('');
  const [creating, setCreating] = useState(false);
  const [createdLink, setCreatedLink] = useState('');

  // List
  const [couples, setCouples] = useState<CoupleRecord[]>([]);
  const [loadingList, setLoadingList] = useState(false);

  const derivedId = autoId && p1Name && p2Name ? `${slug(p1Name)}-${slug(p2Name)}` : coupleId;

  useEffect(() => {
    if (!authed) return;
    loadCouples();
  }, [authed]);

  const loadCouples = async () => {
    setLoadingList(true);
    try {
      const snap = await getDocs(collection(db, 'couples'));
      const list: CoupleRecord[] = snap.docs
        .filter(d => d.data().person1Name)
        .map(d => ({ coupleId: d.id, ...d.data() } as CoupleRecord))
        .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setCouples(list);
    } catch {
      // Firestore may not have any couples yet
    }
    setLoadingList(false);
  };

  const login = () => {
    if (password === ADMIN_PASSWORD) { setAuthed(true); setPwError(''); }
    else setPwError('Yanlış şifre');
  };

  const createCouple = async () => {
    const cid = derivedId.trim();
    if (!cid || !p1Name || !p1Email || !p2Name || !p2Email) return;
    setCreating(true);

    // Create couple metadata doc at couples/{coupleId}
    await setDoc(doc(db, 'couples', cid), {
      coupleId: cid,
      person1Name: p1Name.trim(),
      person1Email: p1Email.trim().toLowerCase(),
      person2Name: p2Name.trim(),
      person2Email: p2Email.trim().toLowerCase(),
      startDate: startDate || null,
      createdAt: serverTimestamp(),
    });

    // Populate couple config
    if (startDate) {
      await setDoc(configDoc(cid), {
        startDate,
        person1Name: p1Name.trim(),
        person2Name: p2Name.trim(),
      }, { merge: true });
    } else {
      await setDoc(configDoc(cid), {
        person1Name: p1Name.trim(),
        person2Name: p2Name.trim(),
      }, { merge: true });
    }

    // Create invite docs for auto-assignment on login
    const sanitizeEmail = (e: string) => e.trim().toLowerCase().replace(/\//g, '_');
    await setDoc(doc(db, 'invites', sanitizeEmail(p1Email)), {
      coupleId: cid,
      displayName: p1Name.trim(),
      createdAt: serverTimestamp(),
    });
    await setDoc(doc(db, 'invites', sanitizeEmail(p2Email)), {
      coupleId: cid,
      displayName: p2Name.trim(),
      createdAt: serverTimestamp(),
    });

    const link = `${window.location.origin}/@${cid}`;
    setCreatedLink(link);
    setCreating(false);

    // Reset form
    setP1Name(''); setP1Email(''); setP2Name(''); setP2Email(''); setStartDate(''); setCoupleId('');
    await loadCouples();
  };

  // ── Password gate ──
  if (!authed) {
    return (
      <div style={styles.gate}>
        <div style={styles.gateCard}>
          <span style={styles.adminMark}>⌘</span>
          <h2 style={styles.gateTitle}>Admin Paneli</h2>
          <input
            type="password"
            placeholder="Şifre"
            value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && login()}
            style={styles.gateInput}
            autoFocus
          />
          {pwError && <p style={{ color: '#ff6b6b', fontSize: 13 }}>{pwError}</p>}
          <motion.button whileTap={{ scale: 0.96 }} onClick={login} style={styles.gateBtn}>
            Giriş
          </motion.button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Admin Paneli</h1>
        <button onClick={() => setAuthed(false)} style={styles.logoutBtn}>Çıkış</button>
      </div>

      {/* Create couple form */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Yeni Çift Oluştur</h2>

        <div style={styles.form}>
          {/* Person 1 */}
          <div style={styles.personBlock}>
            <p style={styles.personLabel}>Kişi 1</p>
            <input placeholder="İsim" value={p1Name} onChange={e => setP1Name(e.target.value)} style={styles.input} />
            <input placeholder="E-posta" value={p1Email} onChange={e => setP1Email(e.target.value)} style={styles.input} type="email" />
          </div>

          {/* Person 2 */}
          <div style={styles.personBlock}>
            <p style={styles.personLabel}>Kişi 2</p>
            <input placeholder="İsim" value={p2Name} onChange={e => setP2Name(e.target.value)} style={styles.input} />
            <input placeholder="E-posta" value={p2Email} onChange={e => setP2Email(e.target.value)} style={styles.input} type="email" />
          </div>

          {/* Couple ID */}
          <div style={styles.idRow}>
            <div style={{ flex: 1 }}>
              <p style={styles.personLabel}>Kişiselleştirilmiş URL</p>
              {autoId ? (
                <div style={styles.derivedId}>{derivedId || 'isimlerden otomatik'}</div>
              ) : (
                <input placeholder="ornek: emre-hande" value={coupleId} onChange={e => setCoupleId(e.target.value)} style={styles.input} />
              )}
            </div>
            <label style={styles.autoLabel}>
              <input type="checkbox" checked={autoId} onChange={e => setAutoId(e.target.checked)} />
              <span>Otomatik</span>
            </label>
          </div>

          {/* Start date */}
          <div>
            <p style={styles.personLabel}>Birliktelik başlangıcı (opsiyonel)</p>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={styles.input} />
          </div>

          <motion.button
            whileTap={{ scale: 0.96 }}
            onClick={createCouple}
            disabled={creating || !derivedId || !p1Name || !p1Email || !p2Name || !p2Email}
            style={{
              ...styles.createBtn,
              opacity: (!derivedId || !p1Name || !p1Email || !p2Name || !p2Email) ? 0.5 : 1,
            }}
          >
            {creating ? 'Oluşturuluyor...' : 'Çifti Oluştur'}
          </motion.button>
        </div>

        {/* Success — show invite link */}
        <AnimatePresence>
          {createdLink && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              style={styles.linkCard}
            >
              <p style={{ fontSize: 13, color: '#67E8F9', fontWeight: 800, marginBottom: 8 }}>Çift oluşturuldu.</p>
              <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 10 }}>Özel URL:</p>
              <div style={styles.linkBox}>{createdLink}</div>
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={() => { navigator.clipboard.writeText(createdLink); }}
                  style={styles.copyBtn}
                >
                  Kopyala
                </motion.button>
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setCreatedLink('')}
                  style={styles.dismissBtn}
                >
                  Kapat
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Couples list */}
      <div style={styles.section}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h2 style={styles.sectionTitle}>Mevcut Çiftler</h2>
          <button onClick={loadCouples} style={styles.refreshBtn}>↻ Yenile</button>
        </div>

        {loadingList ? (
          <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 14 }}>Yükleniyor...</p>
        ) : couples.length === 0 ? (
          <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: 14 }}>Henüz çift yok</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {couples.map(c => <CoupleRow key={c.coupleId} c={c} />)}
          </div>
        )}
      </div>

      {/* Firestore rules reminder */}
      <div style={styles.rulesNote}>
        <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', lineHeight: 1.6 }}>
          Firestore kurallarında <code>invites</code> ve <code>couples</code> koleksiyonlarına yazma iznini açmayı unutma.
          <br />Admin şifresi: <code>REACT_APP_ADMIN_PASSWORD</code> env değişkeni
        </p>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  gate: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  gateCard: {
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 24, padding: '32px 28px', width: '100%', maxWidth: 320,
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14,
  },
  adminMark: {
    width: 54, height: 54, borderRadius: 18, display: 'grid', placeItems: 'center',
    background: 'linear-gradient(135deg, #7C3AED, #4F46E5)', fontSize: 24, fontWeight: 900,
  },
  gateTitle: { fontFamily: "'Playfair Display', serif", fontSize: 22, margin: 0 },
  gateInput: {
    width: '100%', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 14, padding: '12px 16px', color: 'white', fontSize: 16, outline: 'none', boxSizing: 'border-box',
  },
  gateBtn: {
    width: '100%', padding: '13px 0',
    background: 'linear-gradient(135deg, #E91E8C, #9C27B0)',
    border: 'none', borderRadius: 16, color: 'white', fontSize: 15, fontWeight: 700, cursor: 'pointer',
  },
  container: { minHeight: '100vh', padding: '60px 16px 40px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 },
  title: { fontFamily: "'Playfair Display', serif", fontSize: 26, margin: 0 },
  logoutBtn: { background: 'none', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 10, padding: '6px 14px', color: 'rgba(255,255,255,0.5)', fontSize: 13, cursor: 'pointer' },
  section: { marginBottom: 32 },
  sectionTitle: { fontFamily: "'Playfair Display', serif", fontSize: 18, marginBottom: 14, color: 'rgba(255,255,255,0.85)' },
  form: { display: 'flex', flexDirection: 'column', gap: 16 },
  personBlock: { display: 'flex', flexDirection: 'column', gap: 8, background: 'rgba(255,255,255,0.03)', borderRadius: 16, padding: 14 },
  personLabel: { fontSize: 12, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase' as const, letterSpacing: 0.5, marginBottom: 4 },
  input: {
    background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 12, padding: '11px 14px', color: 'white', fontSize: 14, outline: 'none',
    width: '100%', boxSizing: 'border-box' as const,
  },
  idRow: { display: 'flex', gap: 12, alignItems: 'flex-end' },
  derivedId: {
    background: 'rgba(233,30,140,0.08)', border: '1px solid rgba(233,30,140,0.2)',
    borderRadius: 12, padding: '11px 14px', fontSize: 14, fontFamily: 'monospace', color: '#FF6B9D',
  },
  autoLabel: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'rgba(255,255,255,0.5)', cursor: 'pointer', flexShrink: 0, paddingBottom: 4 },
  createBtn: {
    padding: '15px 24px',
    background: 'linear-gradient(135deg, #E91E8C, #9C27B0)',
    border: 'none', borderRadius: 18, color: 'white', fontSize: 15, fontWeight: 700, cursor: 'pointer',
    boxShadow: '0 6px 24px rgba(233,30,140,0.35)',
  },
  linkCard: {
    marginTop: 12, background: 'rgba(30,120,50,0.12)', border: '1px solid rgba(60,180,80,0.25)',
    borderRadius: 16, padding: '16px 18px',
  },
  linkBox: {
    background: 'rgba(255,255,255,0.06)', borderRadius: 10, padding: '10px 14px',
    fontSize: 13, fontFamily: 'monospace', color: '#7dff8f', wordBreak: 'break-all',
  },
  copyBtn: { flex: 1, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, padding: '8px 14px', color: 'white', fontSize: 13, cursor: 'pointer' },
  dismissBtn: { background: 'none', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '8px 14px', color: 'rgba(255,255,255,0.4)', fontSize: 13, cursor: 'pointer' },
  refreshBtn: { background: 'none', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '6px 14px', color: 'rgba(255,255,255,0.5)', fontSize: 13, cursor: 'pointer' },
  rulesNote: { marginTop: 24, padding: 16, background: 'rgba(255,200,0,0.04)', border: '1px solid rgba(255,200,0,0.12)', borderRadius: 14 },
};
